import { HBox } from "by/gamefactory/cityversetycoon/scenes/common/components/ui/HBox";
import { MyWalletsBoxElement, WalletsBoxElementData } from "./MyWalletsBoxElement";
import { CommonResourcesPackage } from "by/gamefactory/cityversetycoon/scenes/common/resources/CommonResourcesPackage";
import { Event } from "event-system";
import { IDestroyOptions } from "pixi.js";
import { ILogger } from "by/gamefactory/cityversetycoon/logger/ILogger";
import { MyWalletsBoxElementType } from "./MyWalletsBoxElementType";
import { MyWalletResourcePackage } from "../../resources/MyWalletResourcePackage";
import { WalletsTooltip } from "./WalletsTooltip";

export class MyWalletsBox extends HBox {
    public readonly onWalletClick: Event<MyWalletsBoxElement>;

    public set currentWallet(wallet: MyWalletsBoxElement) {
        if (this.currentWalletValue === wallet) {
            return;
        }
        this.currentWalletValue = wallet;
        this.onWalletClick.emit(wallet);
    }

    public get currentWallet(): MyWalletsBoxElement {
        return this.currentWalletValue;
    }

    private static readonly CHILD_SPACING = 32;

    private readonly commonResources: CommonResourcesPackage;
    private readonly myWalletResources: MyWalletResourcePackage;
    private readonly wallets: Array<MyWalletsBoxElement>;
    private readonly logger: ILogger;
    private readonly tooltip: WalletsTooltip;

    private currentWalletValue: MyWalletsBoxElement;

    public constructor(
        commonResources: CommonResourcesPackage,
        myWalletResources: MyWalletResourcePackage,
        logger: ILogger
    ) {
        super(MyWalletsBox.CHILD_SPACING);
        this.commonResources = commonResources;
        this.myWalletResources = myWalletResources;
        this.logger = logger;
        this.onWalletClick = new Event();
        this.wallets = [];
        this.tooltip = new WalletsTooltip(this.commonResources, this.myWalletResources);
        this.addChild(this.tooltip);
    }

    public addWallet(data: WalletsBoxElementData): MyWalletsBoxElement {
        if (this.children.length === 6) {
            this.logger.error(`Can not add more then 5 wallets.`);
            return;
        }
        const wallet = this.createWalletBoxElement(data);
        this.wallets.push(wallet);
        wallet.onClick.add(this.onWalletClickHandle, this);
        return this.addChild(wallet);
    }

    public removeWallet(removedWallet: MyWalletsBoxElement): void {
        if (this.currentWalletValue === removedWallet) {
            this.currentWalletValue = null;
        }
        const walletIndex = this.wallets.findIndex((wallet) => wallet === removedWallet);
        this.wallets.splice(walletIndex, 1);
        removedWallet.onClick.remove(this.onWalletClickHandle, this);
        this.removeChild(removedWallet);
    }

    public showTooltip(): void {
        const connectWallet = this.wallets.find((wallet) => wallet.data.type === MyWalletsBoxElementType.CONNECT_WALLET);
        if (connectWallet) {
            this.tooltip.position.set(connectWallet.position.x - this.tooltip.width + 112, -this.tooltip.height);
            this.tooltip.show();
        }
    }

    public hideTooltip(): void {
        this.tooltip.hide();
    }

    private createWalletBoxElement(data: WalletsBoxElementData): MyWalletsBoxElement {
        switch (data.type) {
            case MyWalletsBoxElementType.THIRDWEB: return new MyWalletsBoxElement(this.commonResources.assets_common_ui_web3_thirdweb_icon_png, this.myWalletResources.assets_views_my_wallet_ui_wallet_glow_png, data);
            case MyWalletsBoxElementType.COINBASE: return new MyWalletsBoxElement(this.commonResources.assets_common_ui_web3_coinbase_icon_png, this.myWalletResources.assets_views_my_wallet_ui_wallet_glow_png, data);
            case MyWalletsBoxElementType.METAMASK: return new MyWalletsBoxElement(this.commonResources.assets_common_ui_web3_metamask_icon_png, this.myWalletResources.assets_views_my_wallet_ui_metamask_glow_png, data);
            case MyWalletsBoxElementType.CONNECT_WALLET: return new MyWalletsBoxElement(this.myWalletResources.assets_views_my_wallet_ui_add_wallet_glass_png, this.myWalletResources.assets_views_my_wallet_ui_wallet_glow_png, data);
            default: {
                this.logger.warn(`Unsupported wallet type: ${data.type}`);
            }
        }
    }

    private onWalletClickHandle(wallet: MyWalletsBoxElement): void {
        this.currentWalletValue = wallet;
        this.onWalletClick.emit(wallet);
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.wallets.forEach((wallet) => {
            wallet.onClick.remove(this.onWalletClickHandle, this);
        });
        super.destroy(options);
    }
}
